import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Globe, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BONavigation from "components/admin/BONavigation";
import { FranchiseLogo } from "components/franchise/FranchiseLogo";
import { AnalysisAge, AnalysisCheckButton, daysSince, FRESH_DAYS, STALE_DAYS } from "components/franchise/AnalysisCheck";
import { getFranchises, FranchiseDoc } from "interfaces/establishments";
import { getFranchiseLogo } from "utils/franchiseDetector";

const countOf = (f: FranchiseDoc) => f.establishmentCount ?? 0;

// The URL set on the franchise wins, then a logo bundled in the app (franchiseDetector).
// The detector falls back to an emoji for any name, which is not a logo — so icons don't count.
const hasLogo = (f: FranchiseDoc): boolean => {
    if (f.logoUrl) return true;
    const bundled = getFranchiseLogo(f.name);
    return !bundled.isIcon && typeof bundled.logo === "string";
};

type SortKey = "count" | "analysis";
type Dir = "asc" | "desc";
type AnalysisFilter = "all" | "fresh" | "stale" | "never" | "nologo";

// Each key's natural first direction: biggest networks first, longest-unanalysed first.
const DEFAULT_DIR: Record<SortKey, Dir> = { count: "desc", analysis: "asc" };
const PAGE_SIZES = [25, 50, 100];

/** Never analysed sorts as the oldest possible date, so it heads the "oldest first" order. */
const analyzedTime = (f: FranchiseDoc) => (f.lastAnalyzedAt ? new Date(f.lastAnalyzedAt).getTime() : 0);

const matches = (f: FranchiseDoc, filter: AnalysisFilter) => {
    if (filter === "nologo") return !hasLogo(f);
    const days = daysSince(f.lastAnalyzedAt);
    if (filter === "never") return days === null;
    if (filter === "fresh") return days !== null && days < FRESH_DAYS;
    if (filter === "stale") return days !== null && days >= STALE_DAYS;
    return true;
};

/** Page numbers to draw: always the first and last, the current one and its neighbours, gaps as null. */
function pageWindow(page: number, total: number): (number | null)[] {
    const pages = new Set([1, total, page - 1, page, page + 1].filter(p => p >= 1 && p <= total));
    const sorted = [...pages].sort((a, b) => a - b);
    return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? [null, p] : [p]));
}

export default function FranchiseList() {
    const [allFranchises, setAllFranchises] = useState<FranchiseDoc[]>([]);
    const [showEmpty, setShowEmpty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<{ key: SortKey; dir: Dir }>({ key: "count", dir: "desc" });
    const [filter, setFilter] = useState<AnalysisFilter>("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const navigate = useNavigate();

    useEffect(() => {
        getFranchises().then(setAllFranchises).finally(() => setLoading(false));
    }, []);

    // Any change to what is listed starts again from page 1. Ticking a row does not: the date
    // changing must not throw the admin back to the top of a list they are working through.
    useEffect(() => setPage(1), [search, sort, filter, showEmpty, pageSize]);

    // Filtered here rather than in `getFranchises`, which also feeds the picker in Bulk
    // Franchise — a franchise created a minute ago has no establishment yet and must stay
    // assignable there.
    const visible = useMemo(() => allFranchises.filter(f => showEmpty || countOf(f) > 0), [allFranchises, showEmpty]);
    const emptyCount = allFranchises.filter(f => countOf(f) === 0).length;
    const totalSites = visible.reduce((n, f) => n + countOf(f), 0);
    const countFor = (id: AnalysisFilter) => visible.filter(f => matches(f, id)).length;

    const franchises = useMemo(() => {
        const q = search.trim().toLowerCase();
        const sign = sort.dir === "asc" ? 1 : -1;
        return visible
            .filter(f => (!q || f.name.toLowerCase().includes(q)) && matches(f, filter))
            .sort((a, b) => {
                const diff = sort.key === "count" ? countOf(a) - countOf(b) : analyzedTime(a) - analyzedTime(b);
                // Ties fall back to the bigger network, so equal dates still list what matters first.
                return sign * diff || countOf(b) - countOf(a) || a.name.localeCompare(b.name);
            });
    }, [visible, search, sort, filter]);

    const totalPages = Math.max(1, Math.ceil(franchises.length / pageSize));
    const current = Math.min(page, totalPages);
    const rows = franchises.slice((current - 1) * pageSize, current * pageSize);
    const from = franchises.length === 0 ? 0 : (current - 1) * pageSize + 1;
    const to = Math.min(current * pageSize, franchises.length);

    const sortBy = (key: SortKey) =>
        setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: DEFAULT_DIR[key] });

    const markAnalyzed = (id: string, lastAnalyzedAt: string | null) =>
        setAllFranchises(list => list.map(f => (f._id === id ? { ...f, lastAnalyzedAt } : f)));

    const SortHeader = ({ k, label, align = "left" }: { k: SortKey; label: string; align?: "left" | "right" }) => {
        const active = sort.key === k;
        const Arrow = sort.dir === "asc" ? ArrowUp : ArrowDown;
        return (
            <th className={`px-6 py-3 font-medium ${align === "right" ? "text-right" : ""}`}
                aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                <button
                    onClick={() => sortBy(k)}
                    className={`inline-flex items-center gap-1 rounded-sm transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                        ${active ? "text-ink" : "hover:text-ink"}`}
                >
                    {label}
                    <Arrow className={`h-3.5 w-3.5 ${active ? "" : "opacity-0"}`} />
                </button>
            </th>
        );
    };

    const stats: { id: AnalysisFilter; label: string; value: number; hint: string }[] = [
        { id: "all", label: "Réseaux suivis", value: visible.length, hint: `${totalSites.toLocaleString("fr-FR")} établissements` },
        { id: "fresh", label: "À jour", value: countFor("fresh"), hint: `Analysées il y a moins de ${FRESH_DAYS} j` },
        { id: "stale", label: "À revoir", value: countFor("stale"), hint: `Dernière analyse il y a plus de ${STALE_DAYS} j` },
        { id: "never", label: "Jamais analysées", value: countFor("never"), hint: "Aucune analyse enregistrée" },
    ];

    const tabs: { id: AnalysisFilter; label: string }[] = [
        { id: "all", label: "Toutes" },
        { id: "fresh", label: "À jour" },
        { id: "stale", label: "À revoir" },
        { id: "never", label: "Jamais analysées" },
        { id: "nologo", label: "Sans logo" },
    ];

    return (
        <div className="min-h-screen bg-canvas">
            <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
                <BONavigation />

                {/* header */}
                <div className="mb-6 mt-2">
                    <h1 className="text-2xl font-semibold tracking-tight text-ink">Franchises</h1>
                    <p className="mt-1 text-sm text-ink-muted">
                        Suivez quand chaque réseau a été analysé pour la dernière fois.
                    </p>
                </div>

                {/* stat cards — each one is also a filter */}
                <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {stats.map(s => {
                        const active = filter === s.id;
                        return (
                            <button
                                key={s.id}
                                onClick={() => setFilter(s.id)}
                                aria-pressed={active}
                                className={`rounded-2xl border bg-surface p-5 text-left transition-colors
                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                    ${active ? "border-ink" : "border-border hover:border-border-strong"}`}
                            >
                                <p className="text-sm text-ink-muted">{s.label}</p>
                                <p className="mt-2 text-3xl font-semibold tracking-tight text-ink tabular-nums">
                                    {loading ? <span className="inline-block h-8 w-12 animate-pulse rounded-lg bg-surface-sunken align-middle" /> : s.value.toLocaleString("fr-FR")}
                                </p>
                                <p className="mt-1 truncate text-xs text-ink-faint">{s.hint}</p>
                            </button>
                        );
                    })}
                </div>

                <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                    {/* toolbar */}
                    <div className="flex flex-col gap-3 border-b border-border px-6 pt-4 sm:flex-row sm:items-end sm:justify-between">
                        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Filtrer par analyse">
                            {tabs.map(t => {
                                const active = filter === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setFilter(t.id)}
                                        aria-current={active ? "page" : undefined}
                                        className={`flex items-center gap-2 whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors
                                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                            ${active ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}
                                    >
                                        {t.label}
                                        <span className={`rounded-full px-2 py-0.5 text-xs tabular-nums
                                            ${active ? "bg-ink text-ink-inverse" : "bg-surface-sunken text-ink-muted"}`}>
                                            {countFor(t.id)}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>
                        <div className="relative mb-3 sm:w-72">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Rechercher une franchise"
                                className="h-10 w-full rounded-lg border border-transparent bg-surface-sunken pl-9 pr-9 text-sm text-ink
                                    placeholder:text-ink-faint transition-colors focus:border-border-strong focus:bg-surface
                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            />
                            {search && (
                                <button onClick={() => setSearch("")} aria-label="Effacer la recherche"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-ink-faint hover:text-ink">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="flex animate-pulse items-center gap-4 border-b border-border px-6 py-4 last:border-b-0">
                                    <div className="h-10 w-10 rounded-lg bg-surface-sunken" />
                                    <div className="space-y-2">
                                        <div className="h-3 w-40 rounded-full bg-surface-sunken" />
                                        <div className="h-2.5 w-24 rounded-full bg-surface-sunken" />
                                    </div>
                                    <div className="ml-auto h-3 w-16 rounded-full bg-surface-sunken" />
                                </div>
                            ))}
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            {allFranchises.length === 0 ? (
                                <>
                                    <p className="text-sm font-medium text-ink">Aucune franchise enregistrée</p>
                                    <p className="mt-1 text-sm text-ink-muted">Les franchises sont créées automatiquement lors du tag d'un article</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm font-medium text-ink">Aucune franchise n'a d'établissement</p>
                                    <button onClick={() => setShowEmpty(true)}
                                        className="mt-1 text-sm text-ink-muted underline underline-offset-2 hover:text-ink">
                                        Afficher les {allFranchises.length} franchises vides
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="border-b border-border text-xs text-ink-faint">
                                    <tr>
                                        <th className="px-6 py-3 font-medium">Franchise</th>
                                        <SortHeader k="count" label="Établissements" align="right" />
                                        <SortHeader k="analysis" label="Dernière analyse" />
                                        <th className="px-6 py-3 text-right font-medium">Analysée</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-16 text-center">
                                                <p className="text-sm font-medium text-ink">Aucune franchise ne correspond</p>
                                                <p className="mt-1 text-sm text-ink-muted">Essayez un autre filtre ou une autre recherche.</p>
                                            </td>
                                        </tr>
                                    ) : rows.map(f => (
                                        <tr
                                            key={f._id}
                                            onClick={() => navigate(`/bo/franchises/${f._id}`)}
                                            className="group cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-surface-sunken/50"
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex min-w-0 items-center gap-4">
                                                    <FranchiseLogo f={f} size={40} />
                                                    <div className="min-w-0">
                                                        <p className="truncate font-medium text-ink group-hover:underline group-hover:underline-offset-2">{f.name}</p>
                                                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-ink-faint">
                                                            {f.website ? (
                                                                <>
                                                                    <Globe className="h-3 w-3 flex-shrink-0" />
                                                                    {f.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                                                                </>
                                                            ) : "Pas de site"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="font-semibold text-ink tabular-nums">{countOf(f).toLocaleString("fr-FR")}</span>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <AnalysisAge date={f.lastAnalyzedAt} />
                                                {f.lastAnalyzedAt && (
                                                    <p className="mt-1 text-xs text-ink-faint">
                                                        {new Date(f.lastAnalyzedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <AnalysisCheckButton
                                                    franchiseId={f._id}
                                                    date={f.lastAnalyzedAt}
                                                    onChange={d => markAnalyzed(f._id, d)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* pagination */}
                    {!loading && visible.length > 0 && (
                        <div className="flex flex-col gap-3 border-t border-border px-6 py-4 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                <span className="tabular-nums">
                                    {from}–{to} sur {franchises.length.toLocaleString("fr-FR")}
                                </span>
                                <label className="flex items-center gap-2">
                                    <span className="text-ink-faint">Lignes</span>
                                    <select
                                        value={pageSize}
                                        onChange={e => setPageSize(Number(e.target.value))}
                                        className="h-8 rounded-lg border border-border bg-surface px-2 text-sm text-ink
                                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                {emptyCount > 0 && (
                                    <button onClick={() => setShowEmpty(v => !v)}
                                        className="text-ink-faint underline underline-offset-2 hover:text-ink">
                                        {showEmpty
                                            ? `Masquer les ${emptyCount} sans établissement`
                                            : `Afficher les ${emptyCount} sans établissement`}
                                    </button>
                                )}
                            </div>
                            {totalPages > 1 && (
                                <nav className="flex items-center gap-1" aria-label="Pagination">
                                    <button
                                        onClick={() => setPage(current - 1)}
                                        disabled={current === 1}
                                        aria-label="Page précédente"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface-sunken
                                            disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </button>
                                    {pageWindow(current, totalPages).map((p, i) =>
                                        p === null ? (
                                            <span key={`gap-${i}`} className="w-6 text-center text-ink-faint">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                aria-current={p === current ? "page" : undefined}
                                                className={`h-8 min-w-[2rem] rounded-lg px-2 text-sm font-medium tabular-nums transition-colors
                                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                                    ${p === current ? "bg-ink text-ink-inverse" : "text-ink hover:bg-surface-sunken"}`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                    <button
                                        onClick={() => setPage(current + 1)}
                                        disabled={current === totalPages}
                                        aria-label="Page suivante"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface-sunken
                                            disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </nav>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
